import React, { useState, useCallback } from 'react';

// --- NFA to Regex Conversion Logic (JavaScript port of the Python code) ---
const NFAConverter = () => {
    const EPSILON_SYMBOL = ""; // Represents epsilon (ε)
    const EMPTY_LANGUAGE_SYMBOL = "∅";

    // --- Regex Formatting Helper Methods ---
    const isBalancedParenthesesSimple = (s) => {
        if (s === null || s === undefined) return true; 
        let balance = 0;
        for (const char of s) {
            if (char === '(') balance++;
            else if (char === ')') balance--;
            if (balance < 0) return false;
        }
        return balance === 0;
    };

    const isSingleSymbolInAlphabet = (rStr, alphabet) => {
        return rStr !== null && rStr !== undefined && rStr.length === 1 && alphabet.has(rStr);
    };

    const isParenthesizedExpression = (rStr) => {
        return rStr !== null && rStr !== undefined &&
               rStr.length > 1 &&
               rStr.startsWith("(") && rStr.endsWith(")") &&
               isBalancedParenthesesSimple(rStr.substring(1, rStr.length - 1));
    };

    const isKleeneStarExpression = (rStr, alphabet) => {
        if (rStr === null || rStr === undefined || rStr.length < 2 || !rStr.endsWith("*")) {
            return false;
        }
        const base = rStr.substring(0, rStr.length - 1);
        return isSingleSymbolInAlphabet(base, alphabet) || isParenthesizedExpression(base);
    };

    const isTopLevelUnion = (rStr) => {
        if (rStr === null || rStr === undefined || rStr === EPSILON_SYMBOL || !rStr.includes('|')) {
            return false;
        }
        let balance = 0;
        for (let char_idx = 0; char_idx < rStr.length; char_idx++) {
            const char = rStr[char_idx];
            if (char === '(') balance++;
            else if (char === ')') balance--;
            else if (char === '|' && balance === 0) {
                 if (char_idx > 0 && char_idx < rStr.length -1) { 
                    return true;
                 }
            }
        }
        return false;
    };
    
    const splitByFirstTopLevelUnion = (rStr) => {
        if (rStr === null || rStr === undefined || !rStr.includes('|')) {
            return null;
        }
        let balance = 0;
        for (let i = 0; i < rStr.length; i++) {
            const char = rStr[i];
            if (char === '(') {
                balance++;
            } else if (char === ')') {
                balance--;
            } else if (char === '|' && balance === 0) {
                if (i > 0 && i < rStr.length - 1) {
                    return [rStr.substring(0, i), rStr.substring(i + 1)];
                } else {
                    return null; 
                }
            }
        }
        return null; 
    };

    const findLongestCommonPrefix = (s1, s2) => {
        if (!s1 || !s2) return "";
        let i = 0;
        while (i < s1.length && i < s2.length && s1[i] === s2[i]) {
            i++;
        }
        let lcpCandidate = s1.substring(0, i);
        
        let balance = 0;
        for (const char of lcpCandidate) {
            if (char === '(') balance++;
            else if (char === ')') balance--;
        }

        if (balance !== 0) { 
            let longestBalanced = "";
            let currentSearchBalance = 0;
            for (let k = 0; k < lcpCandidate.length; k++) {
                if (lcpCandidate[k] === '(') currentSearchBalance++;
                else if (lcpCandidate[k] === ')') currentSearchBalance--;
                if (currentSearchBalance === 0) {
                    longestBalanced = lcpCandidate.substring(0, k + 1);
                }
            }
            return longestBalanced; 
        }
        return lcpCandidate; 
    };

    const regexStar = (rStr, alphabet) => {
        if (rStr === null || rStr === undefined) return EPSILON_SYMBOL; 
        if (rStr === EPSILON_SYMBOL) return EPSILON_SYMBOL; 
        if (isKleeneStarExpression(rStr, alphabet)) return rStr; 
        
        if (isSingleSymbolInAlphabet(rStr, alphabet) || isParenthesizedExpression(rStr)) {
            return `${rStr}*`;
        }
        return `(${rStr})*`;
    };

    const regexConcat = (r1Str, r2Str) => {
        if (r1Str === null || r1Str === undefined || r2Str === null || r2Str === undefined) return null; 
        if (r1Str === EPSILON_SYMBOL) return r2Str; 
        if (r2Str === EPSILON_SYMBOL) return r1Str; 
        
        let s1 = r1Str;
        if (isTopLevelUnion(r1Str)) s1 = `(${r1Str})`;
        
        let s2 = r2Str;
        if (isTopLevelUnion(r2Str)) s2 = `(${r2Str})`;
        
        return `${s1}${s2}`;
    };

    const regexUnion = (r1Str, r2Str, alphabet) => {
        if (r1Str === null || r1Str === undefined) return r2Str;
        if (r2Str === null || r2Str === undefined) return r1Str;
        if (r1Str === r2Str) return r1Str;

        if (r1Str === EPSILON_SYMBOL && isKleeneStarExpression(r2Str, alphabet)) return r2Str;
        if (r2Str === EPSILON_SYMBOL && isKleeneStarExpression(r1Str, alphabet)) return r1Str;
        
        const terms = [r1Str, r2Str].sort();
        
        if (EPSILON_SYMBOL === "") {
            if (terms[0] === "" && terms[1] === "") return "";
            return `(${terms[0]}|${terms[1]})`;
        } else {
            return `(${terms[0]}|${terms[1]})`;
        }
    };

    const generateUniqueStateName = (baseName, existingNames) => {
        let name = baseName;
        let i = 0;
        while (existingNames.has(name)) {
            name = `${baseName}_${i}`;
            i++;
        }
        return name;
    };

    const simplifyRegexStringHeuristic = (regexStr, alphabet) => {
        if (regexStr === null || regexStr === undefined) {
            return null;
        }
            
        let currentRegex = regexStr;
        let madeChangeInOuterLoop = true; 

        while(madeChangeInOuterLoop) {
            madeChangeInOuterLoop = false;
            let regexBeforeThisRulePass = currentRegex;

            // Rule 1: Factor common prefix from union: XA|XB -> X(A|B)
            let exprToFactor = currentRegex;
            let wasGloballyParenthesizedDuringFactoring = false; 
            if (isParenthesizedExpression(exprToFactor)) {
                const tempInner = exprToFactor.substring(1, exprToFactor.length - 1);
                if (splitByFirstTopLevelUnion(tempInner)) { 
                    exprToFactor = tempInner;
                    wasGloballyParenthesizedDuringFactoring = true;
                }
            }
            
            const unionParts = splitByFirstTopLevelUnion(exprToFactor);
            if (unionParts) {
                let [p1, p2] = unionParts;
                const lcp = findLongestCommonPrefix(p1, p2);

                if (lcp && lcp !== "") { 
                    let suffix1 = p1.substring(lcp.length);
                    let suffix2 = p2.substring(lcp.length);

                    if (lcp !== p1 || lcp !== p2 || (suffix1 === "" && suffix2 !== "") || (suffix1 !== "" && suffix2 === "")) {
                        suffix1 = suffix1 === "" ? EPSILON_SYMBOL : suffix1;
                        suffix2 = suffix2 === "" ? EPSILON_SYMBOL : suffix2;
                        
                        let factoredUnionPart = regexUnion(suffix1, suffix2, alphabet);
                        let newFactoredRegex = regexConcat(lcp, factoredUnionPart);
                        
                        if (newFactoredRegex !== currentRegex) { 
                            currentRegex = newFactoredRegex;
                            if (wasGloballyParenthesizedDuringFactoring && 
                                !isParenthesizedExpression(currentRegex) && 
                                isTopLevelUnion(currentRegex)) {
                                currentRegex = `(${currentRegex})`;
                            }
                        }
                    }
                }
            }

            if (currentRegex !== regexBeforeThisRulePass) {
                madeChangeInOuterLoop = true;
                regexBeforeThisRulePass = currentRegex; 
            }
            
            // Rule 2: Heuristic for S S* -> S (conditionally)
            const n_rule2 = currentRegex.length;
            for (let k_split_rule2 = 1; k_split_rule2 < n_rule2; k_split_rule2++) {
                const s_candidate_rule2 = currentRegex.substring(0, k_split_rule2);
                const s_star_suffix_rule2 = currentRegex.substring(k_split_rule2);

                if (s_star_suffix_rule2 === s_candidate_rule2 + "*") {
                    if (s_candidate_rule2.includes("*")) { 
                        currentRegex = s_candidate_rule2;
                        break; 
                    }
                }
            }
            if (currentRegex !== regexBeforeThisRulePass) {
                madeChangeInOuterLoop = true;
                regexBeforeThisRulePass = currentRegex;
            }

            // Rule 3: Basic Parenthesis Cleanup
            if (isParenthesizedExpression(currentRegex)) {
                const innerExpr = currentRegex.substring(1, currentRegex.length - 1);
                const innerIsTopLevelUnion = isTopLevelUnion(innerExpr);

                if (isSingleSymbolInAlphabet(innerExpr, alphabet) ||
                    isKleeneStarExpression(innerExpr, alphabet) ||
                    isParenthesizedExpression(innerExpr) || 
                    !innerIsTopLevelUnion 
                ) {
                    if (currentRegex !== innerExpr) {
                        currentRegex = innerExpr;
                    }
                }
            }
            if (currentRegex !== regexBeforeThisRulePass) madeChangeInOuterLoop = true;
            
            if (!madeChangeInOuterLoop) break; 
        }
        
        return currentRegex;
    };
    
    const convertNfaToRegex = (
        originalStatesSet, 
        alphabetSet, 
        rawTransitionsArray, 
        originalStartState, 
        originalAcceptStatesSet
    ) => {
        let R = new Map(); 
        const allCurrentNames = new Set(originalStatesSet);
        const newStartState = generateUniqueStateName("__q_S_new__", allCurrentNames);
        allCurrentNames.add(newStartState);
        const newFinalState = generateUniqueStateName("__q_F_new__", allCurrentNames);
        allCurrentNames.add(newFinalState);

        const tempTransitions = new Map(); 
        for (const [u_raw, symbolInput_raw, v_raw] of rawTransitionsArray) {
            const u = String(u_raw).trim();
            const v = String(v_raw).trim();
            let symbolInput = symbolInput_raw === null || symbolInput_raw === undefined ? '' : String(symbolInput_raw).trim();
            let regexChar = EPSILON_SYMBOL;
            if (symbolInput !== '') {
                const sLower = symbolInput.toLowerCase();
                if (sLower === 'epsilon' || sLower === 'ε') {
                    regexChar = EPSILON_SYMBOL;
                } else if (alphabetSet.has(symbolInput)) {
                    regexChar = symbolInput; 
                } else { regexChar = symbolInput; }
            }
            const key = `${u}->${v}`;
            if (!tempTransitions.has(key)) tempTransitions.set(key, []);
            tempTransitions.get(key).push(regexChar);
        }

        for (const [key, symbolsList] of tempTransitions) {
            if (!symbolsList.length) continue;
            let currentRegex = symbolsList[0];
            for (let i = 1; i < symbolsList.length; i++) {
                currentRegex = regexUnion(currentRegex, symbolsList[i], alphabetSet);
            }
            R.set(key, currentRegex);
        }
        
        const startStateKey = `${newStartState}->${originalStartState}`;
        R.set(startStateKey, regexUnion(R.get(startStateKey) || null, EPSILON_SYMBOL, alphabetSet));

        for (const accState of originalAcceptStatesSet) {
            const accStateKey = `${accState}->${newFinalState}`;
            R.set(accStateKey, regexUnion(R.get(accStateKey) || null, EPSILON_SYMBOL, alphabetSet));
        }

        const statesToEliminate = Array.from(originalStatesSet);
        const allNodesInProcess = new Set(originalStatesSet);
        allNodesInProcess.add(newStartState);
        allNodesInProcess.add(newFinalState);

        for (const qk of statesToEliminate) {
            const nodesForIJLoop = Array.from(allNodesInProcess).filter(q => q !== qk);
            for (const qi of nodesForIJLoop) {
                for (const qj of nodesForIJLoop) {
                    const r_ik_raw = R.get(`${qi}->${qk}`);
                    const r_ik = r_ik_raw === undefined ? null : r_ik_raw;
                    const r_kj_raw = R.get(`${qk}->${qj}`);
                    const r_kj = r_kj_raw === undefined ? null : r_kj_raw;
                    const r_kk_raw = R.get(`${qk}->${qk}`); 
                    const r_kk = r_kk_raw === undefined ? null : r_kk_raw;

                    if (r_ik === null || r_kj === null) continue;
                    
                    const starred_r_kk = regexStar(r_kk, alphabetSet);
                    let term_via_k = regexConcat(r_ik, starred_r_kk);
                    term_via_k = regexConcat(term_via_k, r_kj);
                    
                    if (term_via_k === null) continue;

                    const r_ij_old_key = `${qi}->${qj}`;
                    const r_ij_old_raw = R.get(r_ij_old_key);
                    const r_ij_old = r_ij_old_raw === undefined ? null : r_ij_old_raw;
                    const new_r_ij = regexUnion(r_ij_old, term_via_k, alphabetSet);
                    R.set(r_ij_old_key, new_r_ij);
                }
            }
            const keysToDelete = [];
            for (const key of R.keys()) {
                const [from, to] = key.split("->");
                if (from === qk || to === qk) keysToDelete.push(key);
            }
            for (const key of keysToDelete) R.delete(key);
        }

        let finalRegex = R.get(`${newStartState}->${newFinalState}`) || null;

        if (finalRegex === null) return EMPTY_LANGUAGE_SYMBOL;
        
        let prevSimplifiedRegex = null;
        while (finalRegex !== prevSimplifiedRegex) {
            prevSimplifiedRegex = finalRegex;
            finalRegex = simplifyRegexStringHeuristic(finalRegex, alphabetSet);
        }
        
        return finalRegex;
    };

    return { convertNfaToRegex };
};

export default function App() {
    // Default state for Example 1 (a*b)
    const [statesInput, setStatesInput] = useState("q0,q1,q2");
    const [alphabetInput, setAlphabetInput] = useState("a,b");
    const [transitionsInput, setTransitionsInput] = useState("q0,a,q0\nq0,b,q1\nq1,,q2");
    const [startStateInput, setStartStateInput] = useState("q0");
    const [acceptStatesInput, setAcceptStatesInput] = useState("q2");
    
    const [resultRegex, setResultRegex] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { convertNfaToRegex } = NFAConverter();

    const handleConvert = useCallback(() => {
        setResultRegex("");
        setErrorMessage("");
        try {
            const statesArray = statesInput.split(',').map(s => s.trim()).filter(s => s);
            if (statesArray.length === 0) throw new Error("States cannot be empty.");
            const statesSet = new Set(statesArray);
            const alphabetArray = alphabetInput.split(',').map(s => s.trim()).filter(s => s);
            const alphabetSet = new Set(alphabetArray);
            const startState = startStateInput.trim();
            if (!startState) throw new Error("Start state cannot be empty.");
            if (!statesSet.has(startState)) throw new Error(`Start state '${startState}' must be in the set of states.`);
            const acceptStatesArray = acceptStatesInput.split(',').map(s => s.trim()).filter(s => s);
            if (acceptStatesArray.length === 0) throw new Error("Accept states cannot be empty.");
            const acceptStatesSet = new Set(acceptStatesArray);
            for (const accState of acceptStatesSet) {
                if (!statesSet.has(accState)) throw new Error(`Accept state '${accState}' must be in the set of states.`);
            }
            const transitionsRawLines = transitionsInput.split('\n').filter(line => line.trim() !== "");
            const transitionsArray = transitionsRawLines.map((line, index) => {
                const parts = line.split(',');
                if (parts.length !== 3) throw new Error(`Transition line ${index + 1} ('${line}') is malformed. Expected format: from_state,symbol,to_state`);
                const [from, symbol, to] = parts.map(p => p.trim());
                if (!from || !to) throw new Error(`Transition line ${index + 1} ('${line}') has empty from/to state.`);
                if (!statesSet.has(from)) throw new Error(`Transition line ${index + 1}: from state '${from}' not in defined states.`);
                if (!statesSet.has(to)) throw new Error(`Transition line ${index + 1}: to state '${to}' not in defined states.`);
                return [from, symbol, to]; 
            });
            const regex = convertNfaToRegex( statesSet, alphabetSet, transitionsArray, startState, acceptStatesSet );
            setResultRegex(regex);
        } catch (error) {
            console.error("Conversion Error (React App):", error);
            setErrorMessage(error.message || "An unknown error occurred during conversion.");
        }
    }, [ statesInput, alphabetInput, transitionsInput, startStateInput, acceptStatesInput, convertNfaToRegex ]);

    const handleClear = () => {
        // Reset to Example 1
        setStatesInput("q0,q1,q2");
        setAlphabetInput("a,b");
        setTransitionsInput("q0,a,q0\nq0,b,q1\nq1,,q2");
        setStartStateInput("q0");
        setAcceptStatesInput("q2");
        setResultRegex("");
        setErrorMessage("");
    };
    
    React.useEffect(() => { handleClear(); }, []); 

    const inputClass = "mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm shadow-sm placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none invalid:border-pink-500 invalid:text-pink-600 focus:invalid:border-pink-500 focus:invalid:ring-pink-500";
    const labelClass = "block text-sm font-medium text-slate-700";
    const buttonClass = "px-4 py-2 rounded-md font-semibold text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2";

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col items-center p-4 sm:p-6 lg:p-8 font-sans">
            <div className="w-full max-w-3xl bg-white shadow-xl rounded-lg p-6 md:p-8">
                <h1 className="text-3xl font-bold text-center text-sky-700 mb-8" dir="rtl">مبدل NFA به Regex</h1>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" dir="rtl">
                    <div>
                        <label htmlFor="states" className={labelClass}>وضعیت ها (با "," جدا کنید):</label>
                        <input type="text" id="states" value={statesInput} onChange={(e) => setStatesInput(e.target.value)} className={inputClass} placeholder="q0,q1,q2" />
                    </div>
                    <div>
                        <label htmlFor="alphabet" className={labelClass}>الفبا (با "," جدا کنید):</label>
                        <input type="text" id="alphabet" value={alphabetInput} onChange={(e) => setAlphabetInput(e.target.value)} className={inputClass} placeholder="a,b" />
                    </div>
                    <div>
                        <label htmlFor="startState" className={labelClass}>وضعیت شروع:</label>
                        <input type="text" id="startState" value={startStateInput} onChange={(e) => setStartStateInput(e.target.value)} className={inputClass} placeholder="q0" />
                    </div>
                    <div>
                        <label htmlFor="acceptStates" className={labelClass}>وضعیت فاینال (با "," جدا کنید):</label>
                        <input type="text" id="acceptStates" value={acceptStatesInput} onChange={(e) => setAcceptStatesInput(e.target.value)} className={inputClass} placeholder="q2" />
                    </div>
                </div>
                <div className="mb-8" dir="rtl">
                    <label htmlFor="transitions" className={labelClass}>
                        توابع انتقال (یک تابع در هر خط به فرمت: <code className="text-xs bg-slate-200 p-0.5 rounded">از,سمبل,به</code>; از جای خالی یا  'epsilon' برای ε استفاده کنید):
                    </label>
                    <textarea 
                        id="transitions" 
                        value={transitionsInput} 
                        onChange={(e) => setTransitionsInput(e.target.value)} 
                        className={`${inputClass} min-h-[100px] font-mono text-sm`}
                        placeholder="q0,a,q0&#10;q0,b,q1&#10;q1,,q2" // Placeholder for Ex1
                    />
                     <p className="mt-1 text-xs text-slate-500">مثال برای اپسیلون: <code className="text-xs bg-slate-200 p-0.5 rounded">q1,,q2</code> or <code className="text-xs bg-slate-200 p-0.5 rounded">q1,epsilon,q2</code></p>
                </div>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8" dir="rtl">
                    <button onClick={handleConvert} className={`${buttonClass} bg-sky-600 hover:bg-sky-700 text-white focus:ring-sky-500 w-full sm:w-auto`}>
                        تبدیل به Regex
                    </button>
                    <button onClick={handleClear} className={`${buttonClass} bg-slate-500 hover:bg-slate-600 text-white focus:ring-slate-500 w-full sm:w-auto`}>
                        پاک کردن
                    </button>
                </div>
                {errorMessage && (
                    <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                        <p className="font-semibold">Error:</p>
                        <p>{errorMessage}</p>
                    </div>
                )}
                {resultRegex && (
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold text-slate-700 mb-2" dir="rtl">عبارت منظم خروجی:</h2>
                        <div className="p-3 bg-slate-50 border border-slate-300 rounded-md">
                            <code className="block whitespace-pre-wrap break-all text-indigo-700 font-mono text-sm md:text-base">
                                {resultRegex}
                            </code>
                        </div>
                         <button 
                            onClick={() => {
                                const textArea = document.createElement("textarea");
                                textArea.value = resultRegex;
                                document.body.appendChild(textArea);
                                textArea.select();
                                try { document.execCommand('copy'); } 
                                catch (err) { console.error('Fallback: Oops, unable to copy', err); }
                                document.body.removeChild(textArea);
                            }}
                            className={`${buttonClass} mt-3 bg-emerald-500 hover:bg-emerald-600 text-white text-xs focus:ring-emerald-400`} dir="rtl"
                        >
                            کپی Regex
                        </button>
                    </div>
                )}
                 <footer className="mt-10 text-center text-xs text-slate-500">
                    <p>مبدل NFA به Regex با استفاده از متد حذف حالت(الگوریتم کلینی).</p>
                    <p>برای نوشتن اپسیلون (ε) از جای خالی استفاده کنید (مثل, <code className="text-xs bg-slate-200 p-0.5 rounded">q0,,q1</code>) یا بنویسید "epsilon".</p>
                    <p>زبان خالی توسط "∅" نشان داده میشود. رشته خالی (epsilon) توسط یک regex خالی نشان داده میشود.</p>
                </footer>
            </div>
        </div>
    );
}
